import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, BookOpen, CreditCard, Briefcase } from "lucide-react";
import { api, errMsg, FIELD, fmtDate, fmtTime, fmtMoney } from "@/lib/api";
import { PageHero, Reveal, SectionHeading } from "@/components/common/Reveal";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PILLARS = [
  { icon: BookOpen, title: "Financial Literacy", text: "Budgeting, saving and the money mindset." },
  { icon: CreditCard, title: "Credit Mastery", text: "Understand, build and leverage credit." },
  { icon: Briefcase, title: "Business Building", text: "Launch and structure a business the right way." },
];

const RegisterDialog = ({ event, onClose }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ full_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/register`, { ...f, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(`You're registered for ${event.title}!`);
      qc.invalidateQueries({ queryKey: ["events"] });
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#14051C] border-[#D4AF37]/30 w-[calc(100%-2rem)] max-h-[90svh] overflow-y-auto rounded-2xl" data-testid="event-register-dialog">
        <DialogHeader>
          <p className="eyebrow">Reserve Your Seat</p>
          <DialogTitle className="font-display text-3xl font-medium">{event.title}</DialogTitle>
          <DialogDescription className="text-white/60">{fmtDate(event.date)} · {fmtTime(event.time)} · {event.location}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Field label="Full Name" required><Input className={FIELD} required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="register-name-input" /></Field>
          <Field label="Email" required><Input className={FIELD} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" /></Field>
          <Field label="Phone"><Input className={FIELD} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="register-phone-input" /></Field>
          <button className="btn-gold w-full" disabled={busy} data-testid="register-submit-button">{busy ? "Please wait…" : event.fee ? `Continue to Payment · ${fmtMoney(event.fee)}` : "Confirm Registration"}</button>
          {event.fee > 0 && <p className="text-xs text-white/45 text-center">You'll be taken to Stripe's secure checkout. Your seat is reserved once payment is complete.</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EventCard = ({ e, onRegister }) => {
  const full = e.capacity && e.registered >= e.capacity;
  return (
    <div className="lux-card lift p-8 flex flex-col h-full" data-testid={`event-card-${e.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">{e.category}</span>
        <span className="text-sm text-white/70" data-testid={`event-price-${e.id}`}>{e.fee ? fmtMoney(e.fee) : "Free"}</span>
      </div>
      <h3 className="mt-6 text-3xl font-medium leading-tight">{e.title}</h3>
      <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{e.description}</p>
      <ul className="mt-6 space-y-2 text-sm text-white/75">
        <li className="flex gap-3"><Calendar className="w-4 h-4 text-[#D4AF37]" />{fmtDate(e.date)}</li>
        <li className="flex gap-3"><Clock className="w-4 h-4 text-[#D4AF37]" />{fmtTime(e.time)}</li>
        <li className="flex gap-3"><MapPin className="w-4 h-4 text-[#D4AF37]" />{e.location}</li>
        <li className="flex gap-3"><Users className="w-4 h-4 text-[#D4AF37]" />{e.registered}/{e.capacity} seats reserved</li>
      </ul>
      <button className="btn-gold mt-8" disabled={full} onClick={() => onRegister(e)} data-testid={`event-register-button-${e.id}`}>{full ? "Session Full" : "Register"}</button>
    </div>
  );
};

export default function Academy() {
  const [active, setActive] = useState(null);
  const { data: events = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  return (
    <div data-testid="academy-page">
      <PageHero testid="academy-hero" eyebrow="B2R Financial Freedom Academy" title={<>Your golden ticket to <span className="italic gold-text">financial freedom.</span></>}
        sub="Our education hub for classes, workshops and masterclasses — built to make wealth-building knowledge accessible to everyone." />
      <section className="bg-[#0A0A0A] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}><div className="flex gap-5 items-start border-t border-[#D4AF37]/30 pt-6"><p.icon className="w-7 h-7 text-[#D4AF37] shrink-0" strokeWidth={1.3} /><div><h3 className="text-2xl font-medium">{p.title}</h3><p className="text-sm text-white/60 mt-2">{p.text}</p></div></div></Reveal>
          ))}
        </div>
      </section>
      <section className="bg-black py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Upcoming Sessions" title="Classes & events" className="mb-14" />
          {isLoading ? <p className="text-white/50">Loading sessions…</p> : events.length === 0 ? (
            <p className="text-white/60" data-testid="events-empty">New sessions are coming soon. Follow us on Instagram for announcements.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-list">
              {events.map((e, i) => <Reveal key={e.id} delay={i * 0.08}><EventCard e={e} onRegister={setActive} /></Reveal>)}
            </div>
          )}
        </div>
      </section>
      {active && <RegisterDialog event={active} onClose={() => setActive(null)} />}
    </div>
  );
}
